import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Link2, AlertTriangle, ExternalLink, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../api/client";
import { AdminTabs } from "./AdminDashboard";

export default function AdminTreasury() {
    const [t, setT] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const load = async () => {
        setRefreshing(true);
        try {
            const r = await api.get("/admin/treasury");
            setT(r.data);
        } catch (e) {
            toast.error("failed to load treasury", { description: formatApiError(e.response?.data?.detail) });
        } finally {
            setRefreshing(false);
        }
    };
    useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);

    const explorerUrl = t?.treasury_address && t?.explorer_base
        ? `${t.explorer_base}${t.treasury_address}`
        : null;

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)] bg-grid-fine">
            <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 grid gap-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Admin · Hyperliquid Treasury</div>
                        <h1 className="mt-2 text-2xl sm:text-3xl font-medium tracking-tighter flex items-center gap-3">
                            <Link2 className="w-6 h-6 text-cyan-400" /> Treasury health
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={load}
                            disabled={refreshing}
                            data-testid="treasury-refresh"
                            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-zinc-300 hover:text-cyan-400 border border-[#1F1F24] hover:border-cyan-500/40 px-3 py-2 rounded-sm disabled:opacity-40"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                            Refresh
                        </button>
                        <Link
                            to="/admin"
                            data-testid="treasury-back"
                            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-zinc-400 hover:text-cyan-400 border border-[#1F1F24] hover:border-cyan-500/40 px-3 py-2 rounded-sm"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" /> Admin
                        </Link>
                    </div>
                </div>

                <AdminTabs />

                {t?.balance_critical && (
                    <div className="border border-rose-500/50 bg-rose-500/10 rounded-sm px-4 py-3 flex items-start gap-3" data-testid="treasury-alert-critical">
                        <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                        <div>
                            <div className="text-sm font-semibold text-rose-300">Treasury BELOW floor — attestations paused</div>
                            <p className="text-xs text-rose-300/80 mt-1">
                                Balance {t.balance_usdc} USDC is below the floor of {t.floor} USDC. New BUBE
                                lifecycle events are being recorded locally but NOT attested on-chain. Fund the
                                treasury via the Hyperliquid testnet faucet to resume.
                            </p>
                        </div>
                    </div>
                )}
                {t?.balance_low && !t?.balance_critical && (
                    <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm px-4 py-3 flex items-start gap-3" data-testid="treasury-alert-low">
                        <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                        <div>
                            <div className="text-sm font-semibold text-amber-300">Treasury running low</div>
                            <p className="text-xs text-amber-300/80 mt-1">
                                Top up soon to keep on-chain attestations flowing.
                            </p>
                        </div>
                    </div>
                )}
                {!t && (
                    <div className="text-[11px] font-mono text-zinc-500">loading…</div>
                )}
                {t && !t.attestation_enabled && (
                    <div className="border border-zinc-700 bg-zinc-700/20 rounded-sm px-4 py-3 text-[11px] font-mono text-zinc-400" data-testid="treasury-attestation-disabled">
                        Attestations are currently DISABLED (treasury private key not configured in env).
                    </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <Stat label="Balance · USDC" value={t ? Number(t.balance_usdc ?? 0).toFixed(8) : "—"}
                          accent={t?.balance_critical ? "text-rose-400" : t?.balance_low ? "text-amber-400" : "text-emerald-400"} />
                    <Stat label="Per-event cost" value={t ? `${t.amount_per_event}` : "—"} />
                    <Stat label="Events remaining" value={t?.events_remaining_estimate?.toLocaleString() || "—"} />
                    <Stat label="Floor" value={t ? `${t.floor} USDC` : "—"} accent="text-zinc-300" />
                </div>

                <div className="grid grid-cols-2 gap-2">
                    <Stat label="On-chain events · 24h" value={t?.events_last_24h ?? "—"} accent="text-cyan-400" testid="stat-events-24h" />
                    <Stat label="Failures · 24h" value={t?.failures_last_24h ?? "—"}
                          accent={t && t.failures_last_24h > 0 ? "text-rose-400" : "text-emerald-400"} testid="stat-failures-24h" />
                </div>

                <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-5">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Treasury wallet</div>
                    <div className="mt-2 font-mono text-sm text-zinc-100 break-all" data-testid="treasury-address-display">
                        {t?.treasury_address || "—"}
                    </div>
                    {explorerUrl && (
                        <a
                            href={explorerUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="treasury-explorer-link"
                            className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-cyan-400 hover:text-cyan-300"
                        >
                            View on Hyperliquid testnet explorer
                            <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, accent = "text-zinc-100", testid }) {
    return (
        <div className="bg-[#0A0A0C] border border-[#1F1F24] p-5 rounded-sm" data-testid={testid}>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">{label}</div>
            <div className={`mt-3 font-mono text-2xl sm:text-3xl tracking-tighter ${accent}`}>{value}</div>
        </div>
    );
}
